/**
 * Güvenli fiyat formatlama fonksiyonu
 * NaN değerlerini önler ve geçersiz değerler için fallback sağlar
 */

/**
 * Bir değeri güvenli bir şekilde sayıya çevirir
 * @param {any} value - Formatlanacak değer
 * @param {number} defaultValue - Geçersiz değer durumunda kullanılacak varsayılan değer (varsayılan: 0)
 * @returns {number} - Güvenli sayı değeri
 */
export const safeParseFloat = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  
  // String ise temizle
  if (typeof value === 'string') {
    // "nan", "NaN", "NAN" gibi değerleri kontrol et
    const cleaned = value.trim().toLowerCase();
    if (cleaned === 'nan' || cleaned === 'null' || cleaned === 'undefined') {
      return defaultValue;
    }
    
    // Sadece sayısal karakterleri al
    const numericStr = value.replace(/[^0-9.,-]/g, '').replace(',', '.');
    if (!numericStr) {
      return defaultValue;
    }
    
    const parsed = parseFloat(numericStr);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  
  // Number tipindeyse direkt kontrol et
  if (typeof value === 'number') {
    return isNaN(value) ? defaultValue : value;
  }
  
  // Diğer durumlarda parseFloat dene
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Fiyatı Türk Lirası formatında formatlar
 * @param {any} value - Formatlanacak fiyat değeri
 * @param {Object} options - Formatlama seçenekleri
 * @param {number} options.minimumFractionDigits - Minimum ondalık basamak (varsayılan: 2)
 * @param {number} options.maximumFractionDigits - Maksimum ondalık basamak (varsayılan: 2)
 * @param {string} options.locale - Locale (varsayılan: 'tr-TR')
 * @param {string} options.currency - Para birimi sembolü (varsayılan: '₺')
 * @param {string} options.fallback - Geçersiz değer durumunda gösterilecek metin (varsayılan: null)
 * @returns {string|null} - Formatlanmış fiyat veya fallback değeri
 */
export const formatPrice = (value, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'tr-TR',
    currency = '₺',
    fallback = null
  } = options;
  
  const numValue = safeParseFloat(value);
  
  // Eğer değer 0 ise ve fallback varsa, fallback'i göster
  if (numValue === 0 && fallback !== null) {
    return fallback;
  }
  
  // Geçersiz değer kontrolü (NaN veya 0 ve fallback yok)
  if (isNaN(numValue) || (numValue === 0 && fallback !== null)) {
    return fallback;
  }
  
  try {
    return `${currency}${numValue.toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits
    })}`;
  } catch (error) {
    console.error('formatPrice error:', error, 'value:', value);
    return fallback || `${currency}0,00`;
  }
};

/**
 * Sayıyı güvenli bir şekilde formatlar (fiyat olmadan)
 * @param {any} value - Formatlanacak değer
 * @param {Object} options - Formatlama seçenekleri
 * @returns {string} - Formatlanmış sayı
 */
export const formatNumber = (value, options = {}) => {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    locale = 'tr-TR'
  } = options;
  
  const numValue = safeParseFloat(value);
  
  if (isNaN(numValue)) {
    return '0';
  }
  
  try {
    return numValue.toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits
    });
  } catch (error) {
    console.error('formatNumber error:', error, 'value:', value);
    return '0';
  }
};

/**
 * URL'leri normalize eder - http:// veya https:// ile başlamıyorsa https:// ekler
 * @param {string} url - Normalize edilecek URL
 * @returns {string} - Normalize edilmiş URL
 */
export const normalizeUrl = (url) => {
  if (!url) return url;
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return url;
  
  // Zaten http:// veya https:// ile başlıyorsa olduğu gibi döndür
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  
  // Domain ile başlıyorsa (örn: demo.website-al.com/...) https:// ekle
  if (trimmedUrl.includes('.')) {
    return `https://${trimmedUrl}`;
  }
  
  // Diğer durumlarda olduğu gibi döndür
  return trimmedUrl;
};

/**
 * Resim/video URL'sini ortama göre çözümler.
 * Lokal dev'de localhost:3000 ile gelen dosya adresleri backend serveFile'a yönlendirilir (resimler 8000'de sunulur).
 * @param {string} url - Medya URL'si
 * @param {string} backendBaseUrl - Backend base URL (örn. http://localhost:8000)
 * @returns {string} - Kullanılacak URL
 */
export const resolveMediaUrl = (url, backendBaseUrl) => {
  if (!url || typeof url !== 'string') return url;
  const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (!isDev || !backendBaseUrl) return url;
  // http://localhost:3000/dosya.webp -> backend serveFile
  const m = url.match(/^https?:\/\/localhost:3000\/([^/?#]+)$/i);
  if (m && m[1]) {
    return `${backendBaseUrl.replace(/\/$/, '')}/backend/admin_api.php?action=serveFile&file=${encodeURIComponent(m[1])}`;
  }
  // http://localhost:3000/uploads/xxx -> aynı şekilde dosya adı serveFile'a
  const m2 = url.match(/^https?:\/\/localhost:3000\/uploads\/([^/?#]+)$/i);
  if (m2 && m2[1]) {
    return `${backendBaseUrl.replace(/\/$/, '')}/backend/admin_api.php?action=serveFile&file=${encodeURIComponent(m2[1])}`;
  }
  return url;
};
